import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Subscription } from 'rxjs';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { DiffusionQr, DiffusionQrApiService } from 'src/app/services/event/diffusion-qr-api.service';
import { blobToBase64, downloadBlob, printImage, qrFileName } from 'src/app/utils/qr-image-file';
import { autoHeightSheetOptions } from 'src/app/utils/auto-height-sheet';

export type DiffusionQrStatus = 'loading' | 'ready' | 'error';

export interface DiffusionQrModalProps {
  readonly eventId: number;
  readonly eventSlug: string;
  /** URL a mostrar si el backend no expone X-Diffusion-Qr-Url (CORS). */
  readonly fallbackUrl: string;
}

const HTTP_FORBIDDEN = 403;
const TOAST_DURATION_MS = 3000;

@Component({
  selector: 'app-diffusion-qr-modal',
  templateUrl: './diffusion-qr-modal.component.html',
  styleUrls: ['./diffusion-qr-modal.component.scss'],
  standalone: false
})
export class DiffusionQrModalComponent implements OnInit, OnDestroy {
  @Input() eventId!: number;
  @Input() eventSlug = '';
  @Input() fallbackUrl = '';

  readonly isNative = Capacitor.isNativePlatform();
  readonly status = signal<DiffusionQrStatus>('loading');
  readonly imageUrl = signal<string | null>(null);
  readonly qrUrl = signal('');
  readonly errorKey = signal('diffusion-qr.error-generic');

  private image: Blob | null = null;
  private request?: Subscription;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly api: DiffusionQrApiService,
    private readonly toast: CustomToastService,
    private readonly translate: TranslateService,
  ) {}

  static async open(modalCtrl: ModalController, props: DiffusionQrModalProps): Promise<void> {
    const modal = await modalCtrl.create({
      component: DiffusionQrModalComponent,
      componentProps: { ...props },
      ...autoHeightSheetOptions(),
    });
    await modal.present();
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.releaseImage();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }

  retry(): void {
    this.load();
  }

  get canShareFiles(): boolean {
    return !this.isNative && this.image !== null && !!navigator.canShare?.({ files: [this.toFile(this.image)] });
  }

  async shareNative(): Promise<void> {
    if (!this.image) return;
    try {
      const data = await blobToBase64(this.image);
      const { uri } = await Filesystem.writeFile({
        path: qrFileName(this.eventSlug),
        data,
        directory: Directory.Cache,
      });
      await Share.share({ files: [uri], dialogTitle: this.translate.instant('diffusion-qr.share-print') });
    } catch (error) {
      this.handleShareError(error);
    }
  }

  async shareWeb(): Promise<void> {
    if (!this.image) return;
    try {
      await navigator.share({ files: [this.toFile(this.image)], url: this.qrUrl() });
    } catch (error) {
      this.handleShareError(error);
    }
  }

  download(): void {
    if (this.image) downloadBlob(this.image, qrFileName(this.eventSlug));
  }

  print(): void {
    const url = this.imageUrl();
    if (!url) return;
    try {
      printImage(url);
    } catch {
      this.showShareError();
    }
  }

  private load(): void {
    this.request?.unsubscribe();
    this.status.set('loading');
    this.request = this.api.getDiffusionQr(this.eventId).subscribe({
      next: qr => this.onLoaded(qr),
      error: (error: unknown) => this.onFailed(error),
    });
  }

  private onLoaded(qr: DiffusionQr): void {
    this.releaseImage();
    this.image = qr.image;
    this.imageUrl.set(URL.createObjectURL(qr.image));
    this.qrUrl.set(qr.url ?? this.fallbackUrl);
    this.status.set('ready');
  }

  private onFailed(error: unknown): void {
    const forbidden = error instanceof HttpErrorResponse && error.status === HTTP_FORBIDDEN;
    this.errorKey.set(forbidden ? 'diffusion-qr.error-forbidden' : 'diffusion-qr.error-generic');
    this.status.set('error');
  }

  private releaseImage(): void {
    const url = this.imageUrl();
    if (url) URL.revokeObjectURL(url);
    this.imageUrl.set(null);
    this.image = null;
  }

  private toFile(blob: Blob): File {
    return new File([blob], qrFileName(this.eventSlug), { type: 'image/png' });
  }

  /** Cancelar el share del sistema no es un error a reportar. */
  private handleShareError(error: unknown): void {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    if (error instanceof Error && /cancel/i.test(error.message)) return;
    this.showShareError();
  }

  private showShareError(): void {
    this.toast.show(this.translate.instant('diffusion-qr.share-error'), TOAST_DURATION_MS, 'error');
  }
}
